import { useState, useEffect } from 'react';
import { goalEarnedTotal } from '../utils/goalPoints';

/**
 * Shared leaderboard data loading for dealer and manufacturer portals.
 * @param {object} options
 * @param {Function} options.apiCall
 * @param {string} options.selectedTab
 * @param {number|null|undefined} options.dealerId - when set, scopes campaigns to this dealer
 * @param {boolean} [options.enabled=true] - when false, skips loading
 * @param {boolean} [options.loading=false] - auth loading gate
 */
const useLeaderboards = ({ apiCall, selectedTab, dealerId, enabled = true, loading = false }) => {
  const [leaderboardCampaigns, setLeaderboardCampaigns] = useState([]);
  const [loadingLeaderboardCampaigns, setLoadingLeaderboardCampaigns] = useState(false);
  const [leaderboardGoals, setLeaderboardGoals] = useState({});
  const [loadingLeaderboardGoals, setLoadingLeaderboardGoals] = useState(false);
  const [leaderboardSort, setLeaderboardSort] = useState({ column: 'points', direction: 'desc' });

  const campaignsWithLeaderboards = leaderboardCampaigns;

  const loadCampaignsForLeaderboard = async () => {
    if (!apiCall) return;
    setLoadingLeaderboardCampaigns(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: '100',
        status: 'RUNNING',
      });
      if (dealerId) {
        params.append('dealer_id', dealerId.toString());
      }
      const result = await apiCall('get', `/api/campaigns?${params.toString()}`);
      if (result.success) {
        const all = result.data.campaigns || [];
        setLeaderboardCampaigns(all.filter((c) => c.show_in_leaderboards));
      }
    } catch (error) {
      console.error('Error loading leaderboard campaigns:', error);
      setLeaderboardCampaigns([]);
    }
    setLoadingLeaderboardCampaigns(false);
  };

  const loadLeaderboardGoals = async () => {
    if (leaderboardCampaigns.length === 0 || !apiCall) return;
    setLoadingLeaderboardGoals(true);
    setLeaderboardGoals({});
    try {
      const results = await Promise.all(
        leaderboardCampaigns.map(async (campaign) => {
          const result = await apiCall('get', `/api/campaigns/${campaign.id}/leaderboard`);
          return { campaignId: campaign.id, goals: result.success ? (result.data.goals || []) : [] };
        })
      );
      const goalsMap = {};
      results.forEach(({ campaignId, goals }) => { goalsMap[campaignId] = goals; });
      setLeaderboardGoals(goalsMap);
    } catch (error) {
      console.error('Error loading leaderboard goals:', error);
    }
    setLoadingLeaderboardGoals(false);
  };

  const handleLeaderboardSortChange = (column) => {
    setLeaderboardSort((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortedLeaderboardGoals = (goals) => {
    if (!goals || goals.length === 0) return [];
    const { column, direction } = leaderboardSort;
    const sorted = [...goals].sort((a, b) => {
      let aVal;
      let bVal;
      switch (column) {
        case 'dealer':
          aVal = (a.dealer?.tournament_nickname || a.dealer?.name || '').toLowerCase();
          bVal = (b.dealer?.tournament_nickname || b.dealer?.name || '').toLowerCase();
          return direction === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        case 'points':
          aVal = goalEarnedTotal(a);
          bVal = goalEarnedTotal(b);
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'used':
          aVal = a.used_points || 0;
          bVal = b.used_points || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'value_1':
          aVal = a.value_1 ?? 0;
          bVal = b.value_1 ?? 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'value_2':
          aVal = a.value_2 ?? 0;
          bVal = b.value_2 ?? 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'value_3':
          aVal = a.value_3 ?? 0;
          bVal = b.value_3 ?? 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'status': {
          const aStatus = [a.done_1, a.done_2, a.done_3].filter(Boolean).length;
          const bStatus = [b.done_1, b.done_2, b.done_3].filter(Boolean).length;
          return direction === 'asc' ? aStatus - bStatus : bStatus - aStatus;
        }
        default:
          return 0;
      }
    });
    return sorted;
  };

  useEffect(() => {
    if (!enabled || loading || !apiCall) return;
    const isDealerScope = typeof dealerId === 'number';
    const isCompanyScope = dealerId === null;
    if (!isDealerScope && !isCompanyScope) return;
    loadCampaignsForLeaderboard();
  }, [dealerId, enabled, loading, apiCall]);

  useEffect(() => {
    if (!enabled || loading || !apiCall) return;
    if (selectedTab === 'leaderboards' && leaderboardCampaigns.length > 0) {
      loadLeaderboardGoals();
    }
  }, [enabled, loading, apiCall, selectedTab, leaderboardCampaigns.length]);

  return {
    campaignsWithLeaderboards,
    leaderboardGoals,
    loadingLeaderboardGoals,
    loadingLeaderboardCampaigns,
    leaderboardSort,
    handleLeaderboardSortChange,
    getSortedLeaderboardGoals,
  };
};

export default useLeaderboards;
